import RouteNames from '@/constants/RouteNames';
import { getErrorMessage } from '@/helpers/auth';
import { t } from '@/i18n';
import AppMessages from '@/models/core/AppMessages';
import { supabase } from '@/supabase';
import type { ServiceResponse } from '@/types/core';
import type { Session, User } from '@supabase/supabase-js';
import router from '@/router';

/**
 * Signs up a new user with email and password
 * @param email User's email
 * @param password User's password
 * @returns AuthResponse with session data or error
 */
export async function signUp(email: string, password: string): Promise<ServiceResponse<{ user: User; session: Session } | null>> {
  try {
    const { data, error: err } = await supabase.auth.signUp({
      email,
      password,
    });

    if (err) {
      const error = t(`errors.auth.${err.message}`);
      AppMessages.error(error);
      return { data: null, error };
    }

    if (!data.user || !data.session) {
      const error = 'Sign up successful but no session created';
      AppMessages.error(error);
      return { data: null, error };
    }

    return { data: { session: data.session, user: data.user }, error: null };
  } catch (err: unknown) {
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: null, error };
  }
}

/**
 * Signs in a user with email and password
 * @param email User's email
 * @param password User's password
 * @returns AuthResponse with session data or error
 */
export async function signIn(email: string, password: string): Promise<ServiceResponse<{ user: User; session: Session }>> {
  try {
    const { data, error: err } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (err) {
      const error = t(`errors.auth.${err.message}`);
      AppMessages.error(error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err: unknown) {
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: null, error };
  }
}

/**
 * Signs in a user with Keycloak OAuth
 * @returns AuthResponse with OAuth URL
 */
export async function signInWithKeycloak(): Promise<ServiceResponse<{ provider: string; url: string }>> {
  try {
    const { data, error: err } = await supabase.auth.signInWithOAuth({
      options: {
        redirectTo: `${window.location.origin}/#/auth/callback`,
        scopes: 'openid',
      },
      provider: 'keycloak',
    });

    if (err) {
      const error = t(`errors.auth.${err.message}`);
      AppMessages.error(error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err: unknown) {
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: null, error };
  }
}

/**
 * Signs out the current user
 * @returns AuthResponse indicating success or error
 */
export async function signOut(): Promise<ServiceResponse<boolean>> {
  try {
    const { error: err } = await supabase.auth.signOut();

    router.push({ name: RouteNames.Login });

    if (err) {
      const error = t(`errors.auth.${err.message}`);
      AppMessages.error(error);
      return { data: false, error };
    }

    return { data: true, error: null };
  } catch (err: unknown) {
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: false, error };
  }
}
